"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notificationsApi } from "@/lib/api/notifications";
import {
  NotificationCategory,
  NotificationListData,
  UnreadCountData,
} from "@/lib/types/notification.type";
import { mapNotification } from "@/utils/notifications";
import { logger } from "@/utils/logger";
import { useAuth } from "./useAuth";

/** Query keys for every notification query. `all` is the invalidation root. */
export const notificationKeys = {
  all: ["notifications"] as const,
  unreadCount: ["notifications", "unread-count"] as const,
  lists: ["notifications", "list"] as const,
  list: (
    category: NotificationCategory | "all",
    page: number,
    limit: number
  ) => ["notifications", "list", category, page, limit] as const,
};

const EMPTY_COUNTS: UnreadCountData = {
  transactions: 0,
  services: 0,
  activities: 0,
  promotions: 0,
  total: 0,
};

interface UseNotificationsOptions {
  /** Restrict the list to one category. Omit for all categories combined. */
  category?: NotificationCategory;
  limit?: number;
  /** Set false for consumers that only need the unread counts (e.g. the bell). */
  enableList?: boolean;
}

export function useNotifications(options: UseNotificationsOptions = {}) {
  const { category, limit = 20, enableList = true } = options;
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();

  const [page, setPage] = useState(1);

  // Reset to the first page whenever the active category changes.
  useEffect(() => {
    setPage(1);
  }, [category]);

  const listQuery = useQuery({
    queryKey: notificationKeys.list(category ?? "all", page, limit),
    queryFn: async () => {
      const res = await notificationsApi.list({ category, page, limit });
      return res.data.data;
    },
    enabled: isAuthenticated && enableList,
    placeholderData: (prev) => prev,
  });

  const unreadCountQuery = useQuery({
    queryKey: notificationKeys.unreadCount,
    queryFn: async () => {
      const res = await notificationsApi.unreadCount();
      return res.data.data;
    },
    enabled: isAuthenticated,
  });

  const notifications = useMemo(
    () => (listQuery.data?.items ?? []).map(mapNotification),
    [listQuery.data]
  );

  const invalidateAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: notificationKeys.all });
  }, [queryClient]);

  // ── Mark one as read (optimistic) ─────────────────────────────────
  const markAsReadMutation = useMutation({
    mutationFn: (id: string) => notificationsApi.markAsRead(id),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.all });

      const prevLists = queryClient.getQueriesData<NotificationListData>({
        queryKey: notificationKeys.lists,
      });
      const prevCounts = queryClient.getQueryData<UnreadCountData>(
        notificationKeys.unreadCount
      );

      let affectedCategory: NotificationCategory | null = null;

      queryClient.setQueriesData<NotificationListData>(
        { queryKey: notificationKeys.lists },
        (old) => {
          if (!old?.items) return old;
          return {
            ...old,
            items: old.items.map((item) => {
              if (item.id === id && !item.is_read) {
                affectedCategory = item.category;
                return {
                  ...item,
                  is_read: true,
                  read_at: new Date().toISOString(),
                };
              }
              return item;
            }),
          };
        }
      );

      if (affectedCategory && prevCounts) {
        const cat: NotificationCategory = affectedCategory;
        queryClient.setQueryData<UnreadCountData>(notificationKeys.unreadCount, {
          ...prevCounts,
          [cat]: Math.max(0, prevCounts[cat] - 1),
          total: Math.max(0, prevCounts.total - 1),
        });
      }

      return { prevLists, prevCounts };
    },
    onError: (error, _id, ctx) => {
      logger.error("Failed to mark notification as read:", error);
      ctx?.prevLists?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      if (ctx?.prevCounts) {
        queryClient.setQueryData(notificationKeys.unreadCount, ctx.prevCounts);
      }
    },
    onSettled: invalidateAll,
  });

  // ── Mark all as read (scoped to a category, or everything) ─────────
  const markAllAsReadMutation = useMutation({
    mutationFn: (cat?: NotificationCategory) =>
      notificationsApi.markAllAsRead(cat),
    onError: (error) => {
      logger.error("Failed to mark all notifications as read:", error);
    },
    onSettled: invalidateAll,
  });

  return {
    // List
    notifications,
    meta: listQuery.data?.meta,
    isLoadingNotifications: listQuery.isLoading,
    isFetchingNotifications: listQuery.isFetching,
    notificationsError: listQuery.error,

    // Unread counts (drives tab badges + the bell badge)
    unreadCounts: unreadCountQuery.data ?? EMPTY_COUNTS,
    isLoadingUnreadCounts: unreadCountQuery.isLoading,

    // Pagination
    page,
    setPage,
    limit,

    // Actions
    markAsRead: markAsReadMutation.mutate,
    isMarkingAsRead: markAsReadMutation.isPending,
    markAllAsRead: markAllAsReadMutation.mutate,
    isMarkingAllAsRead: markAllAsReadMutation.isPending,

    invalidateNotifications: invalidateAll,
  };
}
